import * as React from 'react'
import dynamic from 'next/dynamic'

import { spacing, styled } from '@thg-commerce/gravity-theme'
import { Button, Skeleton } from '@thg-commerce/gravity-elements'
import { i18n, useConfig } from '@thg-commerce/enterprise-core'

const BellIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Bell'),
)
const DisableBellIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/BellDisable'),
)

export const StyledButton = styled(Button)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
`

const ButtonSkeleton = styled(Skeleton)`
  width: 100%;
  height: 50px;
`

enum WebViewMessageType {
  CheckNotifyWhenInStockEnabled = 'BIS_NOTIFICATION_ON_LOAD_PRODUCT',
  EnableNotifyWhenInStock = 'BIS_NOTIFICATION_OPT_IN_PRODUCT',
  DisableNotifyWhenInStock = 'BIS_NOTIFICATION_OPT_OUT_PRODUCT',
}

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void }
  }
}

export interface PushNotifyWhenInStockProps {
  updateProductVariants: (params: { sku: string; isOptedIn: boolean }) => void
  isOptedInForNotification?: boolean
  sku: string
}
export const PushNotifyWhenInStock = (props: PushNotifyWhenInStockProps) => {
  const { siteDefinition } = useConfig()

  const i18nText = {
    notifyMeText: i18n('product.outofstocknotification.addtowaitlist.text'),
    removeNotificationText: i18n(
      'product.outofstocknotification.removefromwaitlist.text',
    ),
  }

  const handleMessage = React.useCallback(
    (event: MessageEvent<any>) => {
      if (event.origin !== `https://${siteDefinition.domain}`) {
        return
      }
      const eventData = JSON.parse(event.data)

      if (!eventData.eventCategory) {
        return
      }

      switch (eventData.eventCategory) {
        case WebViewMessageType.CheckNotifyWhenInStockEnabled:
        case WebViewMessageType.EnableNotifyWhenInStock:
          props.updateProductVariants({
            sku: props.sku,
            isOptedIn: eventData.value,
          })
          break
        case WebViewMessageType.DisableNotifyWhenInStock:
          props.updateProductVariants({
            sku: props.sku,
            isOptedIn: !eventData.value,
          })
          break

        default:
          break
      }
    },
    [props.sku],
  )

  const postMessage = React.useCallback(
    (eventCategory: WebViewMessageType) => {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          eventCategory,
          sku: props.sku,
        }),
      )
    },
    [props.sku],
  )

  const checkProductOptIn = React.useCallback(() => {
    if (props.isOptedInForNotification !== undefined) {
      return
    }
    postMessage(WebViewMessageType.CheckNotifyWhenInStockEnabled)
  }, [props.sku])

  const onMessage = React.useCallback((message) => handleMessage(message), [
    props.sku,
  ])

  React.useEffect(() => {
    checkProductOptIn()
    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [props.sku])

  const handleButtonClick = () => {
    const eventCategory = props.isOptedInForNotification
      ? WebViewMessageType.DisableNotifyWhenInStock
      : WebViewMessageType.EnableNotifyWhenInStock

    postMessage(eventCategory)
  }

  if (props.isOptedInForNotification === undefined) {
    return <ButtonSkeleton />
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <StyledButton
        data-testid="add-device-to-waitlist"
        sizing="regular"
        emphasis="high"
        onClick={handleButtonClick}
      >
        {props.isOptedInForNotification ? (
          <React.Fragment>
            <DisableBellIcon
              style={{ marginRight: spacing(1) }}
              aria-hidden="true"
            />
            <span>{i18nText.removeNotificationText}</span>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <BellIcon style={{ marginRight: spacing(1) }} aria-hidden="true" />
            <span>{i18nText.notifyMeText}</span>
          </React.Fragment>
        )}
      </StyledButton>
    </div>
  )
}
